import React, { useCallback } from 'react';
import { useIntl } from 'react-intl-next';

import EditIcon from '@atlaskit/icon/core/edit';
import AddIcon from '@atlaskit/icon/core/add';
import { IconButton } from '@atlaskit/button/new';
import ButtonGroup from '@atlaskit/button/button-group';
import Tooltip from '@atlaskit/tooltip';

import { fireUIAnalytics } from '@atlassian/analytics-bridge';
import { type UIAnalyticsEvent, AnalyticsContext } from '@atlaskit/analytics-next';
import type OriginTracing from '@atlassiansox/origin-tracing';

import messages from '../../../messages';
import type { PageActionHandler } from '../../confluence-page-tree/types';
import type { PageTreeItemProperties } from '../types';
import { pageAnalyticsAttributes } from '../utils';

import ShareButton from './share-button';

interface Props {
	page: PageTreeItemProperties;
	readOnly?: boolean;
	onEditClick?: PageActionHandler;
	onShareToggle?: (pageId: string | null) => void;
	onAddChildClick?: (parentContentId: string) => void;
	stickyRowContentId: string | null;
	origin: OriginTracing;
	titleId?: string;
}

const PageControls = ({
	page,
	readOnly,
	onEditClick,
	onShareToggle,
	onAddChildClick,
	stickyRowContentId,
	titleId,
	origin,
}: Props) => {
	const { formatMessage } = useIntl();
	const handleEditClick = useCallback(
		(_: React.MouseEvent, analyticsEvent: UIAnalyticsEvent) => {
			onEditClick?.(page);
			fireUIAnalytics(
				analyticsEvent,
				'editPageButton',
				origin.toAnalyticsAttributes({
					hasGeneratedId: true,
				}),
			);
		},
		[onEditClick, origin, page],
	);
	const handleAddChildClick = useCallback(
		(_: React.MouseEvent, analyticsEvent: UIAnalyticsEvent) => {
			onAddChildClick?.(page.id);
			fireUIAnalytics(
				analyticsEvent,
				'createChildPageButton',
				origin.toAnalyticsAttributes({
					hasGeneratedId: true,
				}),
			);
		},
		[onAddChildClick, origin, page.id],
	);
	return (
		<ButtonGroup>
			{!readOnly && (
				<Tooltip
					content={formatMessage(page.hasEmbeddedEdit ? messages.edit : messages.editInConfluence)}
					position="top"
				>
					<IconButton
						appearance="subtle"
						icon={EditIcon}
						onClick={handleEditClick}
						testId="confluence-project-pages.page-tree.page-controls.edit-button"
						label="edit"
						id="edit-button"
						aria-labelledby={`edit-button ${titleId}`}
						spacing="compact"
					/>
				</Tooltip>
			)}
			<ShareButton
				page={page}
				onToggle={onShareToggle}
				stickyRowContentId={stickyRowContentId}
				titleId={titleId}
			/>
			{!page.isDraft && !readOnly && (
				<Tooltip content={formatMessage(messages.createChildPage)} position="top">
					<IconButton
						appearance="subtle"
						icon={AddIcon}
						onClick={handleAddChildClick}
						testId="confluence-project-pages.page-tree.page-controls.create-child-page-button"
						label="create child page"
						id="create-child-page-button"
						aria-labelledby={`create-child-page-button ${titleId}`}
						spacing="compact"
					/>
				</Tooltip>
			)}
		</ButtonGroup>
	);
};

export default (props: Props) => (
	<AnalyticsContext
		data={{
			attributes: pageAnalyticsAttributes(props.page),
		}}
	>
		<PageControls {...props} />
	</AnalyticsContext>
);
